/**
 * 20 - Ammunition Barges (10 §2): three barges moored nose to tail along a canal, each sunk by a charge laid
 * from the towpath. A barge's hull cannot be turned, so the canal runs north-south: a dead-end cut on the quiet
 * side of the map, from the north edge down to its winding hole just short of the belt - where the chain of
 * ravines comes out. The towpath (a `road`) follows its near bank, patrolled, and lit by lamps that look north
 * along it; the depot the barges serve is the wired compound, and its side road ends on the towpath. The GOAL
 * ZONE is the moorings, not the depot.
 * A barge opens to a time bomb only (sim/props.ts), and cooks off - but moored a metre apart none takes its
 * neighbour, and a cook-off is not a bomb: each needs its own charge, three in all.
 * Soft quiet rule: the no-contact bonus.
 */

import { PI } from '../../sim/math';
import { PROPS } from '../../sim/props';
import { FLAG } from '../grid';
import { checker, clutter, guard, guardAtDoor, guardsAbout, insideRect, ruleOf, structure } from './helpers';
import { file, lamp, putBlock, squad } from './helpers3a';
import { checkAfloat, checkTargets, cooksOff } from './helpers3aAsserts';
import type { ArchetypeSpec, GoalCtx } from './types';

export const BARGES = 3;
export const lampsFor = (threat: number): number => (threat >= 6 ? 3 : 2);

const WIDTH = 8;
/** Hull 12 m, a metre of water to the next: 7 m from one barge's heart to the next hull, out of a cook-off's 5.6. */
const PITCH = 13;
/**
 * The canal's centre line stands this far outside the depot's quiet side, with the pickup between them: the
 * towpath's edge - the goal zone's - is then 8-17 m from wherever the pickup falls (03 §5.1 step 2 asks 8-20).
 */
const STAND_OFF = 34;
/** Barge and towpath, measured from the centre line toward the depot. */
const MOORING = 1.5;
const TOWPATH = 6.5;
/** The lamps' poles, on the other side of the water: clear of the bank the mask reserves for it. */
const FAR_BANK = 8;

/** The depot's side road ran to the map edge before the canal was cut: now it ends on the towpath. */
function endSideRoad(g: GoalCtx, towX: number): void {
  const gate = g.ctx.plan.gates.find((s) => s.side !== 'south');
  const road = g.map.roads.find((r) => r.points[0]?.[0] === gate?.x && r.points[0]?.[1] === gate?.z);
  if (!gate || !road) return;
  const kept: Array<[number, number]> = [];
  for (let i = 0; i < road.points.length; i++) {
    const p = road.points[i];
    const before = road.points[i - 1];
    if (!p) continue;
    if (before && (before[0] - towX) * (p[0] - towX) <= 0 && before[0] !== p[0]) {
      const t = (towX - before[0]) / (p[0] - before[0]);
      kept.push([towX, Math.round((before[1] + (p[1] - before[1]) * t) * 100) / 100]);
      break;
    }
    kept.push(p);
  }
  road.points = kept;
}

export const ammoBarges: ArchetypeSpec = {
  id: 'ammo-barges',
  name: 'AMMUNITION BARGES',
  nativeBiome: 'temperate',
  baseThreat: 4,
  lengthTiles: 160,
  clockScale: 1,
  startAlert: 0,
  garrison: 'regular',
  perimeter: 'wire',
  sealed: false,
  layout: 'compound',
  // Narrow, so there is room for the canal between the depot and the map's quiet edge.
  size: { w: [36, 40], d: [30, 34] },
  river: false,
  patrolLoop: 'inside',
  breach: { min: 0, max: 0 },

  build(g) {
    const { roadEast } = g.ctx.plan;
    const toDepot = roadEast ? 1 : -1;
    const centre = roadEast ? g.rect.x0 - STAND_OFF : g.rect.x1 + STAND_OFF;
    const goal = g.ctx.bands[g.ctx.bands.length - 1];
    // The winding hole stops short of the belt; the cut runs off the north edge.
    const end = Math.min((goal?.z1 ?? 48) - 5, 46);
    const course = { points: [[centre, -6], [centre, end]] as Array<[number, number]>, width: WIDTH };
    const [west, east] = [centre - WIDTH / 2, centre + WIDTH / 2];
    const clear = !g.map.mask.hitsRect(west - 1, 1, east + 1, end + WIDTH / 2, FLAG.solid | FLAG.trench);
    if (!clear || west < 4 || east > g.ctx.sizeX - 4) g.map.problems.push('ammo-barges: no room to cut the canal');
    g.map.waterBarriers.push({ course, surface: g.kit.water.surface, weakIce: [], crossings: [] });
    g.map.mask.line(course.points, WIDTH / 2 + 1.5, FLAG.water);

    const towX = centre + toDepot * TOWPATH;
    g.map.road([[towX, 2], [towX, end + 5]], 3);
    endSideRoad(g, towX);
    // Nose to tail from the winding hole northward, against the towpath bank.
    const mooringX = centre + toDepot * MOORING;
    const moorings = Array.from(
      { length: BARGES }, (_, i) => ({ kind: 'barge' as const, x: mooringX, z: end - 6 - i * PITCH }),
    );
    const barges = putBlock(g, moorings, FLAG.solid);
    // The canal's foot is where the quiet way in comes out. The budget posts a sentry just south of every target
    // (posts.ts), and for the southern barge that is here, looking down the ravines' mouth: kept clear, as the
    // open end of the quiet way is everywhere else (quietSide.ts), so that post finds no cell to stand on.
    g.map.mask.circle(centre + toDepot * 3, end + 3, 5, FLAG.keep);

    // Lamps on the far bank, clear of the path and of the pickup. They look NORTH along the cut, away from
    // where the ravines come out; the third, from threat 6, looks back down it.
    const lamps = lampsFor(g.threat);
    const spots = [[end - 2, PI], [end - 6 - PITCH, PI], [end - 6 - 2 * PITCH - 4, 0]] as const;
    let lit = 0;
    for (const [z, yaw] of spots.slice(0, lamps)) {
      // Up or down the bank until there is room: the old line of the depot's side road still crosses it somewhere.
      if ([0, -3, 3, -6, 6, -9].some((dz) => lamp(g, centre - toDepot * FAR_BANK, z + dz, yaw) !== null)) lit++;
    }
    if (lit < lamps) g.map.problems.push('ammo-barges: no room for the towpath lamps');
    // The pair on the path turn about at the middle barge. Walked to the canal's foot, they would spend half of
    // every round looking straight down the ravines' mouth: the southern barge is the one a quiet man can get at.
    file(g, squad(g, 2), [{ x: towX, z: 6 }, { x: towX, z: end - 6 - PITCH }]);
    // Gangway sentries at the northern moorings, watching the water. None at the southern barge, and none looking
    // south: that is the ground between the ravine's end and the depot, and a man posted over it would shut the
    // quiet way in (the core's own sentries are kept off it; these are posted before it is chosen).
    const toWater = -toDepot * (PI / 2);
    if (g.threat >= 3) for (const b of barges.slice(g.threat >= 6 ? 1 : 2)) guard(g, towX + toDepot, b.z, toWater);

    // The depot: warehouses, the guard house, the stores waiting to go aboard.
    const warehouse = structure(g, 'hut', { margin: 1.2 });
    structure(g, 'hut', { margin: 1.2 });
    structure(g, 'guardHouse', { margin: 1.2 });
    structure(g, 'alarmPost', { margin: 1 });
    clutter(g, 'crate', 5);
    if (warehouse) guardAtDoor(g, warehouse);
    guardsAbout(g, 1 + g.rng.int(2));
    // The goal zone is the moorings - cut, barges and towpath - not the depot: that is where the job is, where the
    // no-contact bonus is judged to, and what the quiet way in (the ravines come out at the canal's foot) must reach.
    const bank = centre - toDepot * (WIDTH / 2 + 2);
    const pathEdge = towX + toDepot * 1.5;
    const goalZone = { x0: Math.min(bank, pathEdge), z0: 2, x1: Math.max(bank, pathEdge), z1: end + WIDTH / 2 };
    return {
      goalZone,
      objective: {
        kind: 'destroyTargets', label: 'SINK THE BARGES', targetPropIds: barges.map((b) => b.id), minFraction: 1,
        rules: [{ type: 'noContactBonus' }, { type: 'recall', mode: 'normal' }],
      },
    };
  },

  asserts(def, probe) {
    const { check, out } = checker('ammo-barges');
    const barges = checkTargets(check, def, ['barge']);
    check(barges.length === BARGES, `needs ${BARGES} barges, has ${barges.length}`);
    check(def.objective.kind === 'destroyTargets' && def.objective.minFraction === 1, 'every barge must be sunk');
    check(PROPS.barge.wet === true && (PROPS.barge.secondaryBlast ?? 0) > 0, 'a barge floats, and cooks off');
    check(PROPS.barge.durability === 'bombOnly', 'a barge is sunk by a time bomb, nothing less');
    check(ruleOf(def, 'noContactBonus') !== undefined, 'offers the no-contact bonus');
    const canal = def.waterBarriers.find((b) => b.course.points.every((p) => p[0] === b.course.points[0]?.[0]));
    check(canal !== undefined, 'needs a canal running north-south');
    check(barges.every((b) => b.x === barges[0]?.x), 'the barges are moored in one line');
    check(
      barges.every((b) => insideRect(def.goalZone, b.x, b.z)),
      'the goal zone is the moorings: every barge lies in it',
    );
    // The towpath: a north-south road beside the hulls, from which every barge can be reached on dry ground.
    const path = def.roads.find((r) => r.points.length === 2 && r.points[0]?.[0] === r.points[1]?.[0]
      && barges.every((b) => Math.abs((r.points[0]?.[0] ?? 999) - b.x) <= 6));
    check(path !== undefined, 'needs a towpath along the moorings');
    const pathX = path?.points[0]?.[0] ?? 0;
    const zs = (path?.points ?? []).map((p) => p[1]);
    for (const b of barges) {
      checkAfloat(check, def, b);
      check(probe.breach(b.id) === 0, `barge ${b.id} cannot be reached from the towpath`);
      check(
        Math.min(...zs, 9999) <= b.z - 6 && Math.max(...zs, -1) >= b.z + 6,
        `the towpath does not run the length of barge ${b.id}`,
      );
      check(
        barges.every((o) => o.id === b.id || !cooksOff(b, o)),
        `barge ${b.id} would take its neighbour: each needs its own charge`,
      );
    }
    // Patrolled, and lit at night.
    const walked = def.spawns.some((s) => s.dormantUntil === undefined && (s.patrol?.length ?? 0) >= 2
      && (s.patrol ?? []).every((n) => Math.abs(n[0] - pathX) <= 4));
    check(walked, 'the towpath must be patrolled');
    const canalX = canal?.course.points[0]?.[0] ?? pathX;
    const lamps = def.props.filter((p) => p.kind === 'searchlight' && Math.abs(p.x - canalX) <= FAR_BANK + 3);
    check(
      lamps.length >= lampsFor(def.threat),
      `the towpath needs ${lampsFor(def.threat)} lamps over it, has ${lamps.length}`,
    );
    return out;
  },
};
